import { AuthErrorEvent } from '@/components/organisms/dialog/error-dialog/events'
import CrossPageTransition from '@/mixin/cross-page-transition'
import BehaviorConfig from '@/models/app-config/behavior/behavior'
import { Interaction } from '@spa/components/pages/auth/login/model/interaction'
import { vuexStoreGateway } from '@spa/components/pages/auth/login/model/interaction/login/gateway/vuex-store-gateway'
import { LoginPageParts, setUpForState } from '@spa/components/pages/auth/login/model/parts'
import { State } from '@spa/components/pages/auth/login/model/state'
import { PluginMixin } from '@spa/mixins/plugin'
import { identity } from 'fp-ts/lib/function'
import { Mixin, Mixins } from 'vue-mixin-decorator'
import { Emit } from 'vue-property-decorator'
import { SessionDataLoginStateStore } from '../../model/interaction/initialize'

@Mixin
/**
 * @abstract
 */
export class AsLoginForm extends Mixins<PluginMixin & CrossPageTransition>(PluginMixin, CrossPageTransition) {
  /**
   * @abstract mixinされる側で、propsなどで値を注入する必要がある
   */
  readonly next: string

  interaction: Interaction = Interaction.create()
  state: State = this.interaction.state

  email: string = ''
  password: string = ''

  async created() {
    this.interaction = (await this.interaction.initialize(new SessionDataLoginStateStore()))
      .fold(
        (failed): Interaction<State> => {
          if (failed.state === 'already-logged-in') {
            this.$router.push('/mypage')
          }

          return failed
        },
        identity
      )
    this.state = this.interaction.state
  }

  get pageParts(): LoginPageParts {
    return this.pagePartsFor(this.state)
  }
  pagePartsFor(s: State): LoginPageParts {
    // behaviorオブジェクトを経由しているのが外部ログインに対応する依存のためだけなら、
    // もはやbehaviorオブジェクトを経由する必要はない？
    // その場合、appIdモジュールを直接参照すれば良さそう
    return setUpForState(this.behavior.hostId)(s)
  }

  get isLoading(): boolean {
    return this.state === 'loading' || this.state === 'login-succeed'
  }

  async submit() {
    const validated = await this.$validator.validateAll()

    if (!validated) {
      return
    }

    this.state = 'loading'

    this.interaction.login({
      authentication: {
        email: this.email,
        password: this.password,
      },
      gateway: vuexStoreGateway(this.$store),
    }).then(_ => _.fold(
      (failed): Interaction<State> => {
        this.onError(failed.state)

        return failed
      },
      (succeed) => {
        /**
         * decodeURIを経由させる理由は{@see Signup.goToPrevious}を参照
         */
        this.goTo(decodeURI(this.next))

        return succeed
      }
    )).then((updated) => {
      this.interaction = updated
    }).finally(() => {
      this.state = this.interaction.state
    })
  }

  /**
   * @deprecated 外部ログイン用の機能？現状は既に不要？
   */
  get brand(): string | null {
    return typeof this.$route.query.brand === 'string' ? this.$route.query.brand : null
  }

  /**
   * @deprecated appIdが欲しいだけなら、直接AppIdを参照すれば不要？
   */
  get behavior(): BehaviorConfig {
    return BehaviorConfig.createFromBrand(this.brand)
  }

  @Emit('error')
  private onError(s: State): AuthErrorEvent {
    return {
      content: this.pagePartsFor(s).error,
    }
  }
}
